//* 가상계좌 환불계좌 은행 목록 — 취소 모달 드롭다운용.
//* 서버가 name을 그대로 받아 토스 코드로 되돌리므로 프론트는 코드를 몰라도 된다.
//* (스펙: docs/shop-order-vbank-cancel-spec.md)

import { api_ } from '@shared/plugIn/axios';
import { useQuery } from 'react-query';

export const refundBankListUrl = 'public/shop-order/refund-bank';

export interface RefundBankOption {
  code: string;
  name: string;
}

//! 구서버엔 이 엔드포인트가 없어 404가 난다. 목록이 비면 은행을 아예 못 고르므로 같은 표를 프론트에도 둔다.
//! name은 서버 표(tosspayments.bank-code.ts TossBankCode)와 **글자 그대로** 같아야 한다 —
//! 다르면 서버가 은행을 못 알아보고 REFUND_ACCOUNT_INVALID_BANK로 끊는다.
export const REFUND_BANK_FALLBACK: RefundBankOption[] = [
  { code: 'KYONGNAMBANK', name: '경남은행' },
  { code: 'GWANGJUBANK', name: '광주은행' },
  { code: 'LOCALNONGHYEOP', name: '단위농협' },
  { code: 'BUSANBANK', name: '부산은행' },
  { code: 'SAEMAUL', name: '새마을금고' },
  { code: 'SANLIM', name: '산림조합' },
  { code: 'SHINHAN', name: '신한은행' },
  { code: 'SHINHYEOP', name: '신협' },
  { code: 'CITI', name: '씨티은행' },
  { code: 'WOORI', name: '우리은행' },
  { code: 'POST', name: '우체국예금보험' },
  { code: 'SAVINGBANK', name: '저축은행중앙회' },
  { code: 'JEONBUKBANK', name: '전북은행' },
  { code: 'JEJUBANK', name: '제주은행' },
  { code: 'KAKAOBANK', name: '카카오뱅크' },
  { code: 'KBANK', name: '케이뱅크' },
  { code: 'TOSSBANK', name: '토스뱅크' },
  { code: 'HANA', name: '하나은행' },
  { code: 'HSBC', name: '홍콩상하이은행' },
  { code: 'BOA', name: 'Bank of America' },
  { code: 'IBK', name: 'IBK기업은행' },
  { code: 'KOOKMIN', name: 'KB국민은행' },
  { code: 'DAEGUBANK', name: 'iM뱅크(대구)' },
  { code: 'KDBBANK', name: '한국산업은행' },
  { code: 'NONGHYEOP', name: 'NH농협은행' },
  { code: 'SC', name: 'SC제일은행' },
  { code: 'SUHYEOP', name: 'Sh수협은행' },
  { code: 'SUHYEOPLOCALBANK', name: '수협중앙회' },
];

/**
 * 은행 목록. 서버가 주면 그것을, 못 주면(구서버 404·네트워크) 위 폴백을 쓴다.
 * 은행 표는 거의 안 바뀌므로 한 번 받으면 세션 내내 재조회하지 않는다.
 */
export const useRefundBankList = (enabled = true): RefundBankOption[] => {
  const { data } = useQuery(
    refundBankListUrl,
    async () => (await api_.get<RefundBankOption[]>(refundBankListUrl)).data,
    { enabled, staleTime: Infinity, retry: false },
  );
  return data?.length ? data : REFUND_BANK_FALLBACK;
};

//* 자유 입력된 은행명(계좌환불 요청의 account.bank 등)을 드롭다운 항목에 맞춘다.
//* 정규화(공백·대소문자 제거) 후 정식명 그대로 / '은행'·'뱅크' 접미 보정 / 'KB·NH·IBK·Sh' 접두
//* 제거 정식명과의 일치만 인정한다 — 환불계좌는 틀리면 돈이 엉뚱한 곳으로 가므로 부분일치 추측은 안 한다.
//* 못 맞추면 undefined — 호출부가 원문을 그대로 서버에 보내면 서버 별칭 표(우체국·저축은행 등)가 한 번 더 시도한다.
const normalizeBank = (value: string) => value.replace(/\s/g, '').toLowerCase();
const stripBankPrefix = (name: string) => name.replace(/^(kb|nh|ibk|sh|im)/, '');

export const matchRefundBankOption = (
  raw: string | undefined,
  list: RefundBankOption[],
): RefundBankOption | undefined => {
  const target = normalizeBank(raw ?? '');
  if (!target) return undefined;
  const candidates = [target, `${target}은행`, `${target}뱅크`];
  return list.find((option) => {
    const name = normalizeBank(option.name);
    const core = stripBankPrefix(name);
    return candidates.includes(name) || candidates.includes(core);
  });
};
